import { Icon, IconButton } from "@material-ui/core";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import moment from "moment";
import React, { FC, useCallback, useMemo } from "react";
import { OrderLog, PrintStatus } from "../../../../domains/order/agreggates/order/Order";
import { ProdutoTipo } from "Infrastructure/interfaces/PedidoMeep";
import { moneyMask } from "utils/utils";
import styles from "./OrderLogItemV3.module.scss";

interface OrderLogItemV3Props {
  orderLog: OrderLog;
  onClickPreview?: (order: OrderLog) => void;
}

export const OrderLogItemV3: FC<OrderLogItemV3Props> = ({ orderLog, onClickPreview }) => {
  const { kiosk } = useKiosk();

  const onClickPreviewHandler = useCallback(() => {
    onClickPreview?.(orderLog);
  }, [onClickPreview, orderLog]);

  const ticketsCount = useMemo(
    () =>
      orderLog.orderItems
        ?.filter((item) => item.productType !== ProdutoTipo.TaxaServico)
        ?.reduce((acc, it) => (acc += it.ticketIds?.length ?? 0), 0),
    [orderLog.orderItems]
  );

  const cardNumber = orderLog.payment?.cardNumber;
  const lastDigits = cardNumber ? cardNumber.slice(-4) : null;

  const printIcon = useMemo(() => {
    if (orderLog.printStatus === PrintStatus.SUCCESS) return { icon: "print", color: "#399C54" };
    if (orderLog.printStatus === PrintStatus.ERROR) return { icon: "print_disabled", color: "#e57373" };
    return { icon: "hourglass_empty", color: "#bdbdbd" };
  }, [orderLog.printStatus]);

  const nfceIcon = useMemo(() => {
    if (!orderLog.nfce || orderLog.nfce.length === 0) return null;
    const hasEmitted = orderLog.nfce.some((n) => n.danfe);
    return hasEmitted
      ? { icon: "receipt_long", color: "#399C54" }
      : { icon: "receipt_long", color: "#e57373" };
  }, [orderLog.nfce]);

  const isPrintClickable = orderLog.printStatus !== PrintStatus.SUCCESS;

  if (!orderLog?.friendlyId) return null;

  return (
    <div className={styles.OrderLogItemV3}>
      <div>{orderLog.friendlyId}</div>
      <div>{moment(orderLog.createdAt).format("DD/MM/YY HH:mm")}</div>
      <div>{ticketsCount || 1}</div>
      <div>{moneyMask(orderLog?.totalValue ?? 0)}</div>
      <div className={styles.cardCell}>
        {lastDigits ? (
          <>
            <Icon style={{ fontSize: 14, color: "#aaa", marginRight: 4 }}>credit_card</Icon>
            <span>••{lastDigits}</span>
          </>
        ) : orderLog.paymentPix ? (
          <span className={styles.pixBadge}>PIX</span>
        ) : (
          <span style={{ color: "#ccc" }}>—</span>
        )}
      </div>
      <div className={styles.statusCell}>
        <Icon style={{ fontSize: 18, color: orderLog?.paymentApproved ? "#399C54" : "#eb8d13" }}>
          {orderLog?.paymentApproved ? "check_circle" : "warning"}
        </Icon>
        <Icon style={{ fontSize: 18, color: orderLog?.isSynchronized ? "#399C54" : "#bdbdbd" }}>
          {orderLog?.isSynchronized ? "cloud_done" : "cloud_off"}
        </Icon>
        <span
          className={isPrintClickable ? styles.clickableIcon : undefined}
          onClick={isPrintClickable ? onClickPreviewHandler : undefined}
          onTouchEnd={isPrintClickable ? (e) => { e.preventDefault(); onClickPreviewHandler(); } : undefined}
        >
          <Icon style={{ fontSize: 18, color: printIcon.color }}>{printIcon.icon}</Icon>
        </span>
        {nfceIcon && (
          <Icon style={{ fontSize: 18, color: nfceIcon.color }}>{nfceIcon.icon}</Icon>
        )}
      </div>
      <div>
        <IconButton size="small" onClick={onClickPreviewHandler}>
          <Icon style={{ color: kiosk?.isMee ? "#7f47dd" : "#32008E" }}>search</Icon>
        </IconButton>
      </div>
    </div>
  );
};
